// useTasksData.js
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "@/hooks/use-toast";

const LS_KEY = "vox.tasks";
const OFFLINE_KEY = "vox.tasks.offline";
const EVT = "vox:tasks-updated";

const API_STYLE = process.env.REACT_APP_API_STYLE || "backend"; // "backend" | "postgrest"
const DATA_API_BASE = process.env.REACT_APP_DATA_API_BASE || "";
const DATA_API_KEY = process.env.REACT_APP_DATA_API_KEY || "";

function neonHeaders() {
  const h = { "Content-Type": "application/json" };
  if (DATA_API_KEY) {
    h["apikey"] = DATA_API_KEY;
    h["Authorization"] = `Bearer ${DATA_API_KEY}`;
  }
  return h;
}

async function apiList() {
  if (API_STYLE === "postgrest" && DATA_API_BASE) {
    return axios.get(
      `${DATA_API_BASE}/tasks?order=created_at.desc`,
      { headers: neonHeaders() }
    );
  }
  return axios.get("/api/tasks");
}

async function apiCreate(payload) {
  if (API_STYLE === "postgrest" && DATA_API_BASE) {
    return axios.post(
      `${DATA_API_BASE}/tasks`,
      payload,
      { headers: neonHeaders() }
    );
  }
  return axios.post("/api/tasks", payload);
}

async function apiPatch(id, patch) {
  if (API_STYLE === "postgrest" && DATA_API_BASE) {
    return axios.patch(
      `${DATA_API_BASE}/tasks?id=eq.${id}`,
      patch,
      { headers: neonHeaders() }
    );
  }
  return axios.patch(`/api/tasks/${id}`, patch);
}

async function apiDelete(id) {
  if (API_STYLE === "postgrest" && DATA_API_BASE) {
    return axios.delete(
      `${DATA_API_BASE}/tasks?id=eq.${id}`,
      { headers: neonHeaders() }
    );
  }
  return axios.delete(`/api/tasks/${id}`);
}

export function useTasksData() {
  const [tasks, setTasks] = useState([]);

  // ----------------------
  // Load
  // ----------------------
  async function load() {
    try {
      const offline = localStorage.getItem(OFFLINE_KEY) === "1";
      if (offline) {
        const cached = localStorage.getItem(LS_KEY);
        if (cached) {
          setTasks(JSON.parse(cached));
          return;
        }
      }

      const res = await apiList();
      const list = Array.isArray(res.data) ? res.data : [];
      setTasks(list);

      try {
        localStorage.setItem(LS_KEY, JSON.stringify(list));
        localStorage.setItem(OFFLINE_KEY, "0");
      } catch {}
    } catch {
      try {
        const cached = localStorage.getItem(LS_KEY);
        if (cached) setTasks(JSON.parse(cached));
        localStorage.setItem(OFFLINE_KEY, "1");
      } catch {}
    }
  }

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    const handler = () => load();
    window.addEventListener(EVT, handler);
    return () => window.removeEventListener(EVT, handler);
  }, []);

  // ----------------------
  // Mutations
  // ----------------------
  async function add(title) {
    const payload = { title };

    try {
      const res = await apiCreate(payload);
      const created =
        Array.isArray(res.data) ? res.data[0] : res.data;

      setTasks((prev) => {
        const list = [...prev, created];
        try {
          localStorage.setItem(LS_KEY, JSON.stringify(list));
          localStorage.setItem(OFFLINE_KEY, "0");
        } catch {}
        return list;
      });

      toast({
        title: "Task added",
        description: "Task added.",
      });

      window.dispatchEvent(new CustomEvent(EVT));
      return created;
    } catch {
      toast({
        title: "Task failed",
        description: "What task was that again?",
        variant: "destructive",
      });

      const task = {
        id: crypto.randomUUID(),
        title,
        status: "open",
      };

      setTasks((prev) => {
        const list = [...prev, task];
        try {
          localStorage.setItem(LS_KEY, JSON.stringify(list));
          localStorage.setItem(OFFLINE_KEY, "1");
        } catch {}
        return list;
      });

      window.dispatchEvent(new CustomEvent(EVT));
      return task;
    }
  }

  async function toggleCompleted(id) {
    const t = tasks.find((x) => x.id === id);
    if (!t) return;

    const nextStatus = t.status === "completed" ? "open" : "completed";

    try {
      await apiPatch(id, { status: nextStatus });
      setTasks((prev) =>
        prev.map((x) =>
          x.id === id ? { ...x, status: nextStatus } : x
        )
      );

      if (nextStatus === "completed") {
        toast({
          title: "Completed",
          description: "That’s done!",
        });
      }

      try {
        localStorage.setItem(
          LS_KEY,
          JSON.stringify(
            tasks.map((x) =>
              x.id === id ? { ...x, status: nextStatus } : x
            )
          )
        );
      } catch {}
    } catch {
      setTasks((prev) =>
        prev.map((x) =>
          x.id === id ? { ...x, status: nextStatus } : x
        )
      );
      try {
        localStorage.setItem(OFFLINE_KEY, "1");
      } catch {}
    }

    window.dispatchEvent(new CustomEvent(EVT));
  }

  async function saveEdit(id, patch) {
    try {
      await apiPatch(id, patch);
      setTasks((prev) =>
        prev.map((t) => (t.id === id ? { ...t, ...patch } : t))
      );
      try {
        localStorage.setItem(
          LS_KEY,
          JSON.stringify(
            tasks.map((t) =>
              t.id === id ? { ...t, ...patch } : t
            )
          )
        );
      } catch {}
    } catch {
      setTasks((prev) =>
        prev.map((t) => (t.id === id ? { ...t, ...patch } : t))
      );
      try {
        localStorage.setItem(OFFLINE_KEY, "1");
      } catch {}
    }

    window.dispatchEvent(new CustomEvent(EVT));
  }

  async function remove(id) {
    try {
      await apiDelete(id);
      setTasks((prev) => prev.filter((t) => t.id !== id));
    } catch {
      setTasks((prev) => prev.filter((t) => t.id !== id));
      try {
        localStorage.setItem(OFFLINE_KEY, "1");
      } catch {}
    }

    try {
      localStorage.setItem(
        LS_KEY,
        JSON.stringify(tasks.filter((t) => t.id !== id))
      );
    } catch {}

    window.dispatchEvent(new CustomEvent(EVT));
  }

  return { tasks, load, add, toggleCompleted, saveEdit, remove };
}
